/**
 * Здоровье года: возрастной дрейф, болезни, заражение ВИЧ и годовая
 * вероятность смерти.
 *
 * Смертность: qx из модельной таблицы (e0 по полу, u5mr, imr страны на год
 * рождения), умноженная на поправки здоровья и привычек:
 *   qx × exp(0.5·(neutral(age) − health)/50) × (smoking 1.3) × (alcohol 1.15) × (hiv 2.0), ≤ 1.
 * Множители привычек центрированы по распространённости: таблица уже
 * популяционная (в ней есть курящие и ВИЧ-положительные), поэтому при
 * множителе M и доле p носитель получает M/(1+(M−1)p), остальные —
 * 1/(1+(M−1)p); взвешенное среднее равно 1. Доли: курение и ВИЧ — из
 * датасета на год жизни, алкоголь — доля жизней с привычкой в самой модели
 * (ALCOHOL_SHARE, калибровка этапа 1); до 15 лет доля 0 — детская
 * смертность таблицы не содержит привычек. Без центрирования каждая
 * привычка только добавляла риск и средняя жизнь отставала от e0 на ≈ 2 года.
 * Нейтральное здоровье `neutralHealth(age)` — типичная траектория: 70 на
 * старте, дрейф −0.4 после 40 и −0.8 после 65 (через ~50 к 75 годам —
 * константа 50 из плана есть середина этой кривой). Таблица откалибрована
 * по населению, поэтому на типичной траектории фактор равен 1 в любом
 * возрасте и средняя жизнь сходится к e0; при фиксированной точке 50
 * молодость с health 70 удлиняла бы жизнь в странах с высокой ранней
 * смертностью на ~2.5 года. Лёгкая болезнь отнимает 5 только на год
 * болезни (в этот год риск смерти выше), затем здоровье возвращается —
 * иначе 8 %/год давали бы постоянную утечку −0.4/год с рождения и средняя
 * жизнь отставала бы от e0 на ~3 года.
 *
 * Таблицы кэшируются по (e0, u5mr, imr): бинарный поиск b дорог, а в
 * прогоне на тысячи жизней одна страна-год повторяется многократно.
 *
 * `u5mr`/`imr` в датасете на 100 рождений, таблице нужны на 1000.
 * Все случайности — из переданного `Rng`; функции не мутируют вход.
 */
import { getValue, type Dataset } from '@life/data';
import { clampStats } from './focus';
import { buildLifeTable, MAX_AGE, type LifeTable } from './lifetable';
import type { Rng } from './rng';
import type { Life, LogEntry } from './types';

/** Годовой дрейф здоровья после 40 и после 65 лет. */
export const AGE_DRIFT_40 = 0.4;
export const AGE_DRIFT_65 = 0.8;
const DRIFT_AGE_1 = 40;
const DRIFT_AGE_2 = 65;

/** Здоровье на старте типичной траектории (как в birth.ts без поправки на u5mr). */
export const BASE_HEALTH = 70;
/**
 * Восстановление: за год закрывается такая доля отставания от нейтрального
 * здоровья возраста (травмы заживают, после тяжёлого года организм
 * возвращается к норме). Действует только вверх — выше нормы здоровье
 * держат спорт и забота, а вниз его ведёт возрастной дрейф. Без этого
 * каждое событие (авария −12, работа −3/год) отнимало здоровье навсегда,
 * взрослый жил при ~40 против нормы 70 и средняя жизнь отставала от e0
 * на 3.5 года (калибровка Task 13).
 */
export const HEALTH_RECOVERY_RATE = 0.2;
/** Масштаб поправки: exp(0.5·Δ/50), Δ — отставание от нейтрального здоровья. */
const HEALTH_EXP_SCALE = 0.5;
const HEALTH_EXP_SPAN = 50;
const SMOKING_MULT = 1.3;
const ALCOHOL_MULT = 1.15;
const HIV_MULT = 2.0;
/** Доля жизней с алкогольной привычкой в модели (нет показателя в датасете). */
export const ALCOHOL_SHARE = 0.12;
/** Центрирование привычек действует с возраста, когда привычка возможна: у детей доля 0. */
export const HABIT_MIN_AGE = 15;

/** Множитель привычки, центрированный по её доле p: носитель M/(1+(M−1)p), остальные 1/(1+(M−1)p). */
function centredMult(has: boolean, mult: number, share: number): number {
  const p = Number.isFinite(share) ? Math.max(0, Math.min(1, share)) : 0;
  const mean = 1 + (mult - 1) * p;
  return (has ? mult : 1) / mean;
}

export const MINOR_ILLNESS_CHANCE = 0.08;
export const MINOR_ILLNESS_HEALTH = 5;
/** Год лёгкой болезни: на следующий год здоровье возвращается. */
export const MINOR_ILLNESS_FLAG = 'minorIllnessYear';
/** Стоимость лёгкой болезни: 0.1·доход·oopHealth/30. */
const MINOR_ILLNESS_INCOME_SHARE = 0.1;
const MINOR_ILLNESS_OOP_DIVISOR = 30;

export const CHRONIC_CHANCE = 0.015;
export const CHRONIC_HEALTH = 10;
const CHRONIC_MIN_AGE = 40;

/** ВИЧ: заражение возможно при распространённости > 1 % и возрасте 15–45. */
const HIV_MIN_PREVALENCE = 1;
const HIV_MIN_AGE = 15;
const HIV_MAX_AGE = 45;
const HIV_YEARLY_FACTOR = 0.02;

export const CONDITION_HIV = 'hiv';
export const CONDITION_CHRONIC = 'chronic';

const MULT_PER_100 = 10;
const CACHE_LIMIT = 1024;
const tableCache = new Map<string, LifeTable>();

/** Таблица смертности страны по полу на год рождения (с кэшем). */
export function lifeTableFor(ds: Dataset, life: Life): LifeTable {
  const year = life.birthYear;
  const e0 = getValue(ds, life.iso3, life.sex === 'f' ? 'lifeExpF' : 'lifeExpM', year).v;
  const u5mr = getValue(ds, life.iso3, 'u5mr', year).v * MULT_PER_100;
  const imr = getValue(ds, life.iso3, 'imr', year).v * MULT_PER_100;
  const key = `${e0}|${u5mr}|${imr}`;
  const cached = tableCache.get(key);
  if (cached) return cached;
  if (tableCache.size >= CACHE_LIMIT) tableCache.clear();
  const table = buildLifeTable(e0, u5mr, imr);
  tableCache.set(key, table);
  return table;
}

function hasCondition(life: Life, cond: string): boolean {
  return life.conditions.includes(cond);
}

/** Нейтральное здоровье возраста: 70 минус накопленный дрейф; поправка к qx = 1. */
export function neutralHealth(age: number): number {
  const a = Number.isFinite(age) && age > 0 ? age : 0;
  const after40 = Math.max(0, Math.min(a, DRIFT_AGE_2) - DRIFT_AGE_1);
  const after65 = Math.max(0, a - DRIFT_AGE_2);
  return BASE_HEALTH - after40 * AGE_DRIFT_40 - after65 * AGE_DRIFT_65;
}

/** Годовая вероятность смерти с поправками на здоровье и привычки, в [0, 1]. */
export function yearlyMortality(ds: Dataset, life: Life): number {
  if (life.age >= MAX_AGE) return 1;
  const base = lifeTableFor(ds, life).qx(life.age);
  const health = life.stats.health;
  const year = life.birthYear + life.age;
  let q = base * Math.exp((HEALTH_EXP_SCALE * (neutralHealth(life.age) - health)) / HEALTH_EXP_SPAN);
  const adult = life.age >= HABIT_MIN_AGE;
  q *= centredMult(life.habits.smoking, SMOKING_MULT, adult ? getValue(ds, life.iso3, 'smoking', year).v / 100 : 0);
  q *= centredMult(life.habits.alcohol, ALCOHOL_MULT, adult ? ALCOHOL_SHARE : 0);
  q *= centredMult(hasCondition(life, CONDITION_HIV), HIV_MULT, adult ? getValue(ds, life.iso3, 'hiv', year).v / 100 : 0);
  if (!(q > 0)) return 0;
  return q > 1 ? 1 : q;
}

function ageDrift(age: number): number {
  if (age >= DRIFT_AGE_2) return AGE_DRIFT_65;
  if (age >= DRIFT_AGE_1) return AGE_DRIFT_40;
  return 0;
}

function clampMoney(x: number): number {
  return Number.isFinite(x) && x > 0 ? x : 0;
}

/**
 * Здоровье одного года: дрейф → возврат после лёгкой болезни →
 * восстановление к норме → лёгкая болезнь → хроника (после 40) →
 * заражение ВИЧ (15–45, при распространённости > 1 %).
 * Лечение лёгкой болезни оплачивается из денег; нехватка уходит в долг.
 * Возвращает новую жизнь и записи журнала (`illness.*`).
 */
export function applyHealth(ds: Dataset, life: Life, rng: Rng): { life: Life; entries: LogEntry[] } {
  const entries: LogEntry[] = [];
  const year = life.birthYear + life.age;
  let health = life.stats.health - ageDrift(life.age);
  let money = clampMoney(life.money);
  let debt = clampMoney(life.debt);
  let conditions = life.conditions;
  let flags = life.flags;

  // Прошлогодняя лёгкая болезнь прошла: здоровье возвращается.
  if (flags[MINOR_ILLNESS_FLAG] === year - 1) {
    health += MINOR_ILLNESS_HEALTH;
    const { [MINOR_ILLNESS_FLAG]: _gone, ...rest } = flags;
    flags = rest;
  }

  // Восстановление к норме возраста: только если здоровье ниже неё.
  health += HEALTH_RECOVERY_RATE * Math.max(0, neutralHealth(life.age) - health);

  if (rng.chance(MINOR_ILLNESS_CHANCE)) {
    health -= MINOR_ILLNESS_HEALTH;
    flags = { ...flags, [MINOR_ILLNESS_FLAG]: year };
    const income = life.job ? clampMoney(life.job.income) : 0;
    if (income > 0) {
      const oop = clampMoney(getValue(ds, life.iso3, 'oopHealth', year).v);
      const cost = (MINOR_ILLNESS_INCOME_SHARE * income * oop) / MINOR_ILLNESS_OOP_DIVISOR;
      if (cost > money) {
        debt += cost - money;
        money = 0;
      } else {
        money -= cost;
      }
    }
    entries.push({ age: life.age, key: 'illness.minor' });
  }

  if (life.age >= CHRONIC_MIN_AGE && !hasCondition(life, CONDITION_CHRONIC) && rng.chance(CHRONIC_CHANCE)) {
    health -= CHRONIC_HEALTH;
    conditions = [...conditions, CONDITION_CHRONIC];
    entries.push({ age: life.age, key: 'illness.chronic' });
  }

  if (life.age >= HIV_MIN_AGE && life.age <= HIV_MAX_AGE && !hasCondition(life, CONDITION_HIV)) {
    const prevalence = getValue(ds, life.iso3, 'hiv', year).v;
    if (prevalence > HIV_MIN_PREVALENCE && rng.chance((prevalence / 100) * HIV_YEARLY_FACTOR)) {
      conditions = [...conditions, CONDITION_HIV];
      entries.push({ age: life.age, key: 'illness.hiv' });
    }
  }

  return {
    life: {
      ...life,
      stats: clampStats({ ...life.stats, health }),
      money: clampMoney(money),
      debt: clampMoney(debt),
      conditions,
      flags,
    },
    entries,
  };
}
